// zr-call — the single entry point most applications import. Wires zr-core,
// zr-webrtc and zr-push together with sane defaults and exposes a small public API.

import {
  EventBus,
  deriveCallPhase,
  generatePeerId,
  isUsablePeerId,
  type CallMode,
  type CallPhase,
  type CallEndReason,
  type IceConnectionState,
  type Room,
  type SignalMessage,
  type SignalingClient,
  type SignalingProvider,
  WebSocketSignalingClient,
  PollSignalingClient,
  ReactionThrottle,
  RaisedHandRoster,
  isAllowedReaction,
  isE2eeSupported,
  normalizeEffect,
  isNoOpEffect,
  createScheduledCall,
  toICS,
  generatePeerIdentity,
  isIdentitySupported,
  fingerprint as computeFingerprint,
  safetyNumber as computeSafetyNumber,
  type PeerIdentity,
  type PeerHeartbeat,
  type DeviceContext,
  type DeviceFingerprint,
  type DeviceLocation,
  type DeviceNetworkIntelligence,
  type DeviceProxyAssessment,
  type DeviceSessionAttestation,
  type DeviceSecurityAttestation,
  type ProxyStatus,
  type RaisedHand,
  type ReactionEmoji,
  type VideoEffect,
  type VideoEffectType,
  type ScheduledCall,
  type CreateScheduledCallInput,
  resolveCallUx,
  orderIceServersUdpFirst,
  stunFromTurnUris,
  throughputMbps,
  LinkCapacityTracker,
  resolveProductProfile,
  validateProductConfig,
  PresenceDirectory,
  type LinkCapacitySample,
  type CallUxChoice,
  type CallUxStyle,
  type BillingMode,
  type PeerRole,
  type AvailabilityStatus,
  type ProductProfile,
  type DirectoryPeer,
  type DirectoryPeerUpdate,
} from 'zr-core';
import {
  selectTransport,
  BroadcastController,
  WhipClient,
  E2eeContext,
  type PlatformAdapter,
  type IceConfig,
  type Transport,
  type TransportDeps,
  type BroadcastTarget,
  type ScreenComposite,
  type ScreenCompositeOptions,
} from 'zr-webrtc';
import type { InviteChannel } from 'zr-push';

export function sanitizeAuthBaseUrl(url: string): string {
  if (!url) return '';
  let cleaned = url.trim();
  cleaned = cleaned.replace(/\/+$/, '');
  const indicators = ['/api/authsvc', '/v1/region', '/v1/credentials'];
  for (const indicator of indicators) {
    const idx = cleaned.toLowerCase().indexOf(indicator);
    if (idx !== -1) {
      cleaned = cleaned.substring(0, idx);
      break;
    }
  }
  return cleaned;
}

export interface ZrClientConfig {
  signaling: SignalingClient;
  platform: PlatformAdapter;
  ice: IceConfig;
  token: string;
  /** Optional WHIP/WHEP media-plane auth token; keep separate from signaling auth. */
  mediaToken?: string;
  /**
   * Signaling Infrastructure Provider. Pin it to deliberately choose the media plane:
   *   • 'serverless-matchmaker' — raw WebRTC P2P + CoTurn + polling (≤10 participants).
   *   • 'whip-whep'            — stream ingress & egress via the ZeroRate Media Kit (>10).
   * Leave unset to let the SDK infer it from room size/mode (back-compat default).
   */
  provider?: SignalingProvider;
  livekitUrl?: string;
  whipEndpoint?: string;
  whepEndpoint?: string;
  invites?: InviteChannel;
  selfId?: string;
  /** Optionally supply a pre-acquired local MediaStream. If set, the client publishes
   *  THIS exact stream, so app-side mute/camera toggles on it reach remote peers. */
  localStream?: MediaStream;
  /** Custom Webhook Endpoint URL to receive live developer telemetry feeds */
  telemetryWebhookUrl?: string;
  /** Authentication header token/secret for the telemetry Webhook */
  telemetryWebhookSecret?: string;
  /** Authentication & Authorization base URL */
  authBaseUrl?: string;
  /** ZeroRate App Public Key */
  publicAppKey?: string;
  /** Region Code (slug) */
  regionCode?: string;
  /** Service Category */
  serviceCategory?: string;
  /** How long an outgoing call rings before giving up as 'no-answer' (ms, default 45000). */
  ringTimeoutMs?: number;
  /** Keep-alive / state-machine sampling cadence in ms (clamped to 3–5 s, default 4 s). */
  keepAliveMs?: number;
  /**
   * End-to-end encryption for the P2P path. Supply a shared secret (a 256-bit key or a
   * passphrase) the participants agree on out-of-band; the relay/SFU then only ever sees
   * ciphertext. Requires a browser with Insertable Streams (Chrome/Edge); silently a no-op
   * elsewhere. `client.e2eeActive` reports whether it is really engaged.
   */
  e2ee?: { secret: Uint8Array | string };
  /** Optional reaction rate-limit window in ms (default 600). */
  reactionThrottleMs?: number;
  /**
   * Device-context controls. Use a trusted server-side/native resolver for public-IP
   * intelligence (country/city/ISP/proxy); the browser alone cannot verify that.
   */
  deviceContext?: DeviceContextOptions;
  /**
   * Per-participant public-key identity (P2P). Each peer announces an ECDH public key so the
   * SDK can surface a stable fingerprint + a verifiable safety number per peer (MITM check).
   * Enabled by default where WebCrypto is available; set `false` to opt out.
   */
  peerIdentity?: boolean;
  /**
   * Native call-UI experience the app wants to render (via `mountCallUI` in @zr/adapter-web,
   * or a native renderer). The engine is UI-agnostic and only carries the preference:
   *   • 'whatsapp'    — Secure P2P 1:1 (E2EE): fullscreen + self-PiP, compact round controls.
   *   • 'google-meet' — Mesh room / managed conference: tiled grid + full Meet control rail.
   *   • 'auto' (default) — 1:1 `call` → WhatsApp, `group`/`broadcast` → Google Meet.
   * `glass` toggles the frosted-glass styling (default true). Read back via `client.ui`.
   */
  ui?: { style?: CallUxChoice; glass?: boolean };
  /**
   * ICE transport policy. `'all'` (default) tries direct P2P (host/srflx over UDP) first and only
   * relays when that fails — this is what gets you a UDP media path most often. Set `'relay'` to
   * FORCE the TURN relay (e.g. locked-down enterprise networks where direct never works, or to
   * hide client IPs); note that removes the direct-UDP path, so leave it `'all'` unless required.
   */
  iceTransportPolicy?: 'all' | 'relay';
  /**
   * Real-time RAW LINK CAPACITY probe (fast.com-style), distinct from the passive media throughput
   * in `quality-changed`. While a call is active the SDK periodically downloads/uploads an
   * incompressible payload against `url` (default: a session-scoped `/api/signaling/speedtest`
   * co-located with the CoTURN edge) and emits a `link-capacity` event with down/up Mbps + latency.
   * `enabled: false` turns it off. Point `url` at a relay-adjacent speed endpoint for path accuracy.
   */
  linkCapacity?: {
    enabled?: boolean;
    url?: string;
    /** How often to probe (ms, default 30000). Kept low-frequency so it doesn't steal call bandwidth. */
    intervalMs?: number;
    /** Download probe size in bytes (default 3,000,000; capped server-side at 5MB). */
    downloadBytes?: number;
    /** Upload probe size in bytes (default 1,000,000). */
    uploadBytes?: number;
  };
  /**
   * Product plan (the top-level state-machine switch):
   *   • 'connectra_basic' (default) — ZeroRate Connectra: Raw WebRTC P2P + polling, ≤10, WhatsApp-style.
   *   • 'connectra_plus'            — ZeroRate Connectra+: WHIP/WHEP media manager for CXaaS contact centres.
   * Pins the provider + participant cap and validates the rest of the config against the plan.
   */
  billingMode?: BillingMode;
  /**
   * Connectra+ ONLY. The peer's role in the contact centre: 'client' (caller/publisher),
   * 'operator' (agent/consumer), or 'admin' (supervisor: monitor + escalate). Drives the call queue.
   */
  peerRole?: PeerRole;
  /** Connectra+ operators/admins: initial availability surfaced in the directory (default 'active'). */
  availability?: AvailabilityStatus;
}

export interface JoinOptions {
  roomId: string;
  mode?: CallMode;
  max?: number;
  password?: string;
  accepted?: boolean;
}

export type FingerprintData = DeviceFingerprint;
export type LocationData = DeviceLocation;

export interface DeviceContextResolverInput {
  fingerprint: DeviceFingerprint;
  location: DeviceLocation | null;
  authBaseUrl?: string;
}

export type DeviceContextNetworkResolver = (
  input: DeviceContextResolverInput,
) => Promise<DeviceNetworkIntelligence | null>;

export interface DeviceContextOptions {
  requestLocationConsent?: boolean;
  networkResolver?: DeviceContextNetworkResolver;
}

export interface HttpDeviceContextNetworkResolverOptions {
  url: string;
  getToken?: () => string | undefined;
  headers?: Record<string, string>;
  fetch?: typeof fetch;
}

function uniqueStrings(values: (string | undefined)[]): string[] {
  return Array.from(new Set(values.filter((value): value is string => !!value && value.trim().length > 0)));
}

function normalizeProxyStatus(value: unknown): ProxyStatus {
  switch (String(value ?? '').toLowerCase()) {
    case 'clear':
    case 'not-detected':
    case 'clean':
    case 'none':
      return 'clear';
    case 'detected':
    case 'proxy':
    case 'vpn':
    case 'tor':
    case 'hosting':
      return 'detected';
    case 'suspected':
    case 'possible':
      return 'suspected';
    default:
      return 'unknown';
  }
}

function sanitizeProxyAssessment(value: any): DeviceProxyAssessment | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const proxy = !!(value.proxy ?? value.is_proxy);
  const vpn = !!(value.vpn ?? value.is_vpn);
  const tor = !!(value.tor ?? value.is_tor);
  const hosting = !!(value.hosting ?? value.is_hosting);
  const relay = !!(value.relay ?? value.is_relay);
  const status =
    normalizeProxyStatus(value.status) !== 'unknown'
      ? normalizeProxyStatus(value.status)
      : proxy || vpn || tor || hosting || relay
        ? 'detected'
        : 'unknown';
  return {
    status,
    proxy,
    vpn,
    tor,
    hosting,
    relay,
    source: value.source || 'resolver',
    reason: value.reason ? String(value.reason) : undefined,
  };
}

function sanitizeNetworkIntelligence(value: any): DeviceNetworkIntelligence | null {
  if (!value || typeof value !== 'object') return null;
  const proxy = sanitizeProxyAssessment(value.proxy ?? value.proxyAssessment);
  const source = value.source || 'resolver';
  const confidence = ['low', 'medium', 'high'].includes(String(value.confidence))
    ? value.confidence
    : proxy?.status === 'detected'
      ? 'high'
      : 'medium';
  const network: DeviceNetworkIntelligence = {
    publicIp: value.publicIp || value.ip || value.address,
    countryCode: value.countryCode || value.country_code,
    country: value.country,
    region: value.region || value.state,
    city: value.city,
    isp: value.isp || value.provider,
    organization: value.organization || value.org,
    asn: value.asn != null ? Number(value.asn) : undefined,
    timezone: value.timezone,
    latitude: value.latitude != null ? Number(value.latitude) : undefined,
    longitude: value.longitude != null ? Number(value.longitude) : undefined,
    networkType: value.networkType || value.network_type,
    connectionType: value.connectionType || value.connection_type,
    proxy,
    source,
    confidence,
    resolvedAt: value.resolvedAt != null ? Number(value.resolvedAt) : Date.now(),
    warnings: Array.isArray(value.warnings) ? value.warnings.map(String) : undefined,
  };
  return network;
}

function createBlankSecurityAttestation(): DeviceSecurityAttestation {
  const supported = isE2eeSupported();
  return {
    dtlsTransport: false,
    e2eeRequested: false,
    e2eeSupported: supported,
    e2eeActive: false,
    mode: 'transport-dtls',
    verification: supported ? 'partial' : 'unsupported',
    reason: 'No active media session yet.',
  };
}

function createBlankDeviceContextAttestation(): DeviceSessionAttestation {
  return {
    transport: {
      directPeerToPeer: null,
      usingTurn: null,
      usingTcp: null,
      evidence: 'unknown',
    },
    turn: {
      alive: null,
      turnReachable: null,
      credentialsValid: null,
    },
    security: createBlankSecurityAttestation(),
    peerSummary: {
      totalPeers: 0,
      activePeers: 0,
      directPeers: 0,
      relayedPeers: 0,
      tcpPeers: 0,
    },
    proxyStatus: 'unknown',
    locality: 'unknown',
    warnings: ['Browser-only telemetry cannot prove ISP/city/proxy status without a trusted resolver or native feed.'],
    updatedAt: Date.now(),
  };
}

export function createHttpNetworkIntelligenceResolver(
  options: HttpDeviceContextNetworkResolverOptions,
): DeviceContextNetworkResolver {
  return async ({ fingerprint, location }) => {
    const fetchImpl = options.fetch ?? globalThis.fetch?.bind(globalThis);
    if (!fetchImpl) throw new Error('No fetch implementation available for device-context network resolution.');
    const token = options.getToken?.();
    const res = await fetchImpl(options.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
      body: JSON.stringify({ fingerprint, location }),
    });
    let payload: any = {};
    try {
      payload = await res.json();
    } catch {
      payload = {};
    }
    if (!res.ok) {
      throw new Error(payload?.error || payload?.message || `Device-context resolver failed (${res.status}).`);
    }
    return sanitizeNetworkIntelligence(payload?.network || payload);
  };
}

export async function gatherDeviceContext(
  requestLocationConsent: boolean = true,
  options: DeviceContextOptions & { authBaseUrl?: string } = {},
): Promise<DeviceContext> {
  const nav = typeof navigator !== 'undefined' ? (navigator as any) : undefined;
  const connection = nav?.connection;
  const uaBrands = Array.isArray(nav?.userAgentData?.brands)
    ? nav.userAgentData.brands
        .map((brand: any) => brand?.brand && brand?.version ? `${brand.brand}/${brand.version}` : brand?.brand)
        .filter(Boolean)
    : [];
  const fingerprint: FingerprintData = {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    screenResolution: typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : 'unknown',
    colorDepth: typeof window !== 'undefined' ? window.screen.colorDepth : 0,
    language: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'unknown',
    hardwareConcurrency: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 0 : 0,
    deviceMemory: typeof navigator !== 'undefined' ? nav?.deviceMemory : undefined,
    connectionType: connection ? connection.effectiveType || connection.type : 'unknown',
    downlinkMbps: connection?.downlink,
    networkRttMs: connection?.rtt,
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
    devices: [],
    userAgentBrands: uaBrands,
    mobile: !!nav?.userAgentData?.mobile,
  };

  if (typeof navigator !== 'undefined' && navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function') {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      fingerprint.devices = devs.map(d => ({
        kind: d.kind,
        label: d.label || 'Permission Required',
        deviceId: d.deviceId
      }));
    } catch (e) {
      // ignore media devices lists errors
    }
  }

  let location: LocationData | null = null;
  let locationError: string | undefined;

  if (requestLocationConsent && typeof navigator !== 'undefined' && navigator.geolocation) {
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
      location = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        timestamp: pos.timestamp,
        altitude: pos.coords.altitude,
        heading: pos.coords.heading,
        speed: pos.coords.speed,
        source: 'browser',
      };
    } catch (err: any) {
      locationError = err.message || String(err);
    }
  } else {
    locationError = 'Geolocation not requested or not supported';
  }

  let network: DeviceNetworkIntelligence | null = null;
  let networkError: string | undefined;
  if (options.networkResolver) {
    try {
      network = sanitizeNetworkIntelligence(
        await options.networkResolver({ fingerprint, location, authBaseUrl: options.authBaseUrl }),
      );
    } catch (err: any) {
      networkError = err?.message || String(err);
    }
  }

  const notes = uniqueStrings([
    locationError ? 'GPS location is unavailable until the participant grants consent.' : undefined,
    !network
      ? 'Country/city/ISP/proxy details need a trusted server-side resolver or native device feed.'
      : undefined,
  ]);

  return {
    capturedAt: Date.now(),
    fingerprint,
    location,
    locationError,
    network,
    networkError,
    attestation: createBlankDeviceContextAttestation(),
    notes,
  };
}

export class ZrClient {
  readonly events = new EventBus();
  private transport?: Transport;
  private _provider?: SignalingProvider;
  private _localStream?: MediaStream;

  // ---- Call lifecycle (WhatsApp flow) ----
  private _callPhase: CallPhase = 'idle';
  private _endReason?: CallEndReason;
  private invited = false; // an invite handshake is outstanding (in or out)
  private accepted = false; // both sides committed
  private ended = false;
  private remoteId?: string; // the 1:1 counterpart
  private currentRoomId?: string;
  private _roomMode?: CallMode; // last established room mode (drives 'auto' UI resolution)
  private _roomMax?: number;
  private peerIce = new Map<string, IceConnectionState>();
  private peerHeartbeats = new Map<string, PeerHeartbeat>();

  // ---- Product / role / presence directory ----
  private directory = new PresenceDirectory();
  private _availability: AvailabilityStatus;
  private incoming?: { roomId: string; from: string; displayName?: string; password?: string };
  private ringTimer?: any;
  private turnRefreshing = false; // guards against concurrent credential re-mints

  // ---- Raw link-capacity probe (fast.com-style, session-scoped) ----
  private linkCapacityTracker = new LinkCapacityTracker();
  private linkCapacityTimer?: any;
  private linkCapacityProbing = false;
  private lastLinkCapacity?: LinkCapacitySample;
  private unsubscribeSignaling?: () => void;
  private screenShare?: { screenStream: MediaStream; composite?: ScreenComposite; cameraTrack: MediaStreamTrack | null };
  public deviceContext: DeviceContext | null = null;
  public turnLiveness: { alive: boolean; turnReachable: boolean; credentialsValid: boolean; expiresAt?: string | number } | null = null;
  private lastDeviceContextSignature?: string;

  // ---- In-call interactions (reactions + raised hands) ----
  private reactionThrottle: ReactionThrottle;
  private handRoster = new RaisedHandRoster();
  private _handRaised = false;

  // ---- Per-participant public-key identity ----
  private identity?: PeerIdentity;
  private peerPublicKeys = new Map<string, string>(); // peerId -> announced base64url public key
  private identityAnnouncedTo = new Set<string>();

  // ---- E2E encryption + video effects ----
  private e2ee?: E2eeContext;
  private _e2eeActive = false;
  private _videoEffect: VideoEffect = { type: 'none' };
  private effectPipeline?: { track: MediaStreamTrack; stop(): void; sourceTrack: MediaStreamTrack; previewStream?: MediaStream };
  private _rawCameraTrack?: MediaStreamTrack;

  constructor(private cfg: ZrClientConfig) {
    if (this.cfg.authBaseUrl) {
      this.cfg.authBaseUrl = sanitizeAuthBaseUrl(this.cfg.authBaseUrl);
    }
    this._localStream = cfg.localStream;
    this.reactionThrottle = new ReactionThrottle(cfg.reactionThrottleMs);
    this._availability = cfg.availability ?? 'active';
    // If a product plan is pinned, validate the config against it (fail loud on a mismatch such as
    // a contact-centre role on Raw P2P). Unset billingMode stays permissive (provider-driven).
    if (cfg.billingMode) {
      validateProductConfig({ billingMode: cfg.billingMode, peerRole: cfg.peerRole, provider: cfg.provider });
    }

    // Guarantee a unique selfId. Two participants sharing an id deadlock perfect-
    // negotiation (polite/impolite is resolved by comparing ids), so a missing or
    // reserved/default id is replaced with a generated collision-resistant one.
    if (!isUsablePeerId(this.cfg.selfId)) {
      const generated = generatePeerId();
      console.warn(
        `ZeroRate RTC: selfId "${this.cfg.selfId ?? ''}" is missing or not unique; ` +
          `generated "${generated}". Provide a stable, unique selfId per participant.`,
      );
      this.cfg.selfId = generated;
    }
    
    // Auto-setup web-to-native globally-exposed bindings
    if (typeof window !== 'undefined') {
      (window as any).ZeroRateRTC = this;
      
      const EB = (window as any).EdgeBlasterRTC || {};
      EB.instance = this;
      EB.createClient = createClient;
      EB.WebSocketSignalingClient = WebSocketSignalingClient;
      EB.PollSignalingClient = PollSignalingClient;
      (window as any).EdgeBlasterRTC = EB;
    }

    // Connect WebView Event Bridge & Telemetry
    this.events.on((e) => {
      if (e.type === 'provider-selected') {
        this._provider = e.provider;
        this.emitDeviceContext();
      }

      // Fold native per-peer ICE state and departures into the single call phase.
      if (e.type === 'peer-connection-state-changed') {
        if (e.state === 'closed') this.peerIce.delete(e.participantId);
        else this.peerIce.set(e.participantId, e.state);
        this.recomputeCallPhase();
        this.emitDeviceContext();
      } else if (e.type === 'participant-joined') {
        this.directory.upsert({ peerId: e.participant.id, online_status: 'online', displayName: e.participant.displayName }, Date.now());
        this.events.emit({ type: 'presence-changed', peers: this.directory.snapshot() });
      } else if (e.type === 'participant-left') {
        this.peerIce.delete(e.participantId);
        this.peerHeartbeats.delete(e.participantId);
        this.directory.markOffline(e.participantId, Date.now());
        this.events.emit({ type: 'presence-changed', peers: this.directory.snapshot() });
        // A leaver's raised hand must not linger in the roster.
        if (this.handRoster.isRaised(e.participantId)) {
          const roster = this.handRoster.remove(e.participantId);
          this.events.emit({ type: 'hand-raised-changed', participantId: e.participantId, raised: false, raisedHands: roster });
        }
        this.recomputeCallPhase();
        if (this.remoteId && this.remoteId === e.participantId) {
          console.log(`ZeroRate RTC: Remote participant ${e.participantId} left, ending 1:1 call.`);
          this.setEnded('remote-hangup');
          void this.leave();
        }
        this.emitDeviceContext();
      } else if (e.type === 'peer-heartbeat') {
        this.peerHeartbeats.set(e.participantId, e.heartbeat);
        // Fold the live per-peer transport into the directory's turns_status (relay reachability).
        const turns_status = e.heartbeat.alive ? 'online' : 'unreachable';
        const e2ee_status = this._e2eeActive ? 'active' : (this.cfg.e2ee ? 'inactive' : 'unknown');
        this.directory.upsert({ peerId: e.participantId, online_status: 'online', turns_status, e2ee_status: e2ee_status as any }, Date.now());
        this.events.emit({ type: 'presence-changed', peers: this.directory.snapshot() });
        this.emitDeviceContext();
      } else if (e.type === 'turn-liveness-changed') {
        this.turnLiveness = {
          alive: e.alive,
          turnReachable: e.turnReachable,
          credentialsValid: e.credentialsValid,
          expiresAt: e.expiresAt
        };
        if (!e.credentialsValid) {
          // Expired TURN credentials cannot be recovered by an ICE restart — re-mint them
          // and hot-swap onto the live connections so the session survives.
          void this.refreshTurnCredentials();
        }
        this.emitDeviceContext();
      } else if (e.type === 'turn-credentials-expiring') {
        // Proactive rotation: credentials are nearing expiry. Re-mint + hot-swap NOW, before
        // they lapse, so the rollover is seamless (no media blip). Guarded against concurrency.
        console.log(`ZeroRate RTC: TURN credentials expiring in ${Math.round(e.inMs / 1000)}s — rotating proactively.`);
        void this.refreshTurnCredentials(true);
      } else if (e.type === 'peer-reconnect-escalate') {
        // A peer stayed down across repeated ICE restarts. The relay allocation is likely dead
        // or the credentials lapsed — an ICE restart can't fix that. Re-mint TURN credentials and
        // hot-swap a fresh relay onto the live connections (updateIceConfig restarts ICE). This is
        // the recovery that breaks the "restarting ICE forever" storm seen in the field.
        console.warn(`ZeroRate RTC: reconnection to ${e.participantId} failing (attempt ${e.attempts}) — re-minting TURN credentials for a fresh relay.`);
        void this.refreshTurnCredentials(false);
      }

      this.sendToBridge('telemetry-event', e);
      if (this.cfg.telemetryWebhookUrl) {
        this.streamWebhook('telemetry-event', e).catch(() => {});
      }
    });

    // Listen for call-control signaling (invite/accept/decline/cancel/hangup). The
    // media transport separately handles offer/answer/candidate on the same signaling link.
    this.unsubscribeSignaling = this.cfg.signaling.on((msg) => this.handleControlMessage(msg));

    // Auto-connect signaling channel immediately to listen for incoming invites (WhatsApp VoIP flow)
    this.cfg.signaling.connect(this.cfg.token).catch((err) => {
      console.warn('ZeroRate RTC: Failed to auto-connect signaling channel on initialization:', err);
    });

    // Run async contextual device fingerprints and consented location discovery loop
    this.initDeviceContext();
  }

  private async initDeviceContext() {
    try {
      await this.refreshDeviceContext(true);
    } catch (err) {
      console.warn('Failed to retrieve device context:', err);
    }
  }

  private currentSecurityAttestation(): DeviceSecurityAttestation {
    const e2eeRequested = !!this.cfg.e2ee?.secret;
    const e2eeSupported = isE2eeSupported();
    const dtlsTransport = !!this.transport;
    const e2eeActive = !!this._e2eeActive && dtlsTransport;
    if (e2eeActive) {
      return {
        dtlsTransport,
        e2eeRequested,
        e2eeSupported,
        e2eeActive,
        mode: 'insertable-streams-e2ee',
        verification: 'verified',
      };
    }
    if (e2eeRequested && !e2eeSupported) {
      return {
        dtlsTransport,
        e2eeRequested,
        e2eeSupported: false,
        e2eeActive: false,
        mode: 'transport-dtls',
        verification: 'unsupported',
        reason: 'Insertable Streams are unavailable on this runtime, so only DTLS/SRTP transport encryption is active.',
      };
    }
    if (e2eeRequested) {
      return {
        dtlsTransport,
        e2eeRequested: true,
        e2eeSupported,
        e2eeActive: false,
        mode: 'transport-dtls',
        verification: 'partial',
        reason: 'E2EE was requested but is not active on the current media path.',
      };
    }
    return {
      dtlsTransport,
      e2eeRequested: false,
      e2eeSupported,
      e2eeActive: false,
      mode: 'transport-dtls',
      verification: e2eeSupported ? 'partial' : 'unsupported',
      reason: 'The session is protected in transit by DTLS/SRTP, but app-level end-to-end media encryption was not requested.',
    };
  }

  private buildDeviceContextAttestation(): DeviceSessionAttestation {
    const security = this.currentSecurityAttestation();
    const heartbeats = Array.from(this.peerHeartbeats.values());
    const primary =
      heartbeats.find((hb) => hb.alive && (hb.iceConnectionState === 'connected' || hb.iceConnectionState === 'completed')) ||
      heartbeats[0];
    const transport = primary?.transport;
    const totalPeers = heartbeats.length;
    const activePeers = heartbeats.filter((hb) => hb.alive).length;
    const relayedPeers = heartbeats.filter((hb) => hb.transport?.usingTurn).length;
    const directPeers = heartbeats.filter((hb) => hb.transport && !hb.transport.usingTurn).length;
    const tcpPeers = heartbeats.filter((hb) => hb.transport?.usingTcp).length;
    const networkProxyStatus = this.deviceContext?.network?.proxy?.status || 'unknown';
    const locality =
      this.provider === 'whip-whep'
        ? 'relayed'
        : transport
          ? transport.usingTurn
            ? 'relayed'
            : 'direct'
          : 'unknown';
    const warnings = uniqueStrings([
      !this.deviceContext?.network
        ? 'Country/city/ISP/proxy details are unverified until a trusted server-side resolver or native device feed is attached.'
        : undefined,
      networkProxyStatus === 'detected'
        ? 'The network resolver reported a proxy, VPN, Tor exit, or hosting relay on this session.'
        : networkProxyStatus === 'suspected'
          ? 'The network resolver suspects an upstream proxy or VPN on this session.'
          : undefined,
      this.provider === 'whip-whep'
        ? 'Managed conference mode is server-assisted by design; the media path is not direct device-to-device.'
        : transport?.usingTurn
          ? 'Media is currently relayed through TURN, so the active path is not direct local P2P.'
          : undefined,
      security.reason,
    ]);

    return {
      provider: this.provider,
      roomId: this.currentRoomId,
      transport: {
        participantId: primary?.participantId,
        directPeerToPeer: transport ? !transport.usingTurn : null,
        usingTurn: transport?.usingTurn ?? null,
        usingTcp: transport?.usingTcp ?? null,
        localCandidate: transport?.localCandidate,
        remoteCandidate: transport?.remoteCandidate,
        protocol: transport?.protocol,
        relayProtocol: transport?.relayProtocol,
        iceConnectionState: primary?.iceConnectionState,
        connectionState: primary?.connectionState,
        evidence: transport ? 'peer-heartbeat' : 'unknown',
      },
      turn: {
        alive: this.turnLiveness?.alive ?? null,
        turnReachable: this.turnLiveness?.turnReachable ?? null,
        credentialsValid: this.turnLiveness?.credentialsValid ?? null,
        expiresAt: this.turnLiveness?.expiresAt,
      },
      security,
      peerSummary: {
        totalPeers,
        activePeers,
        directPeers,
        relayedPeers,
        tcpPeers,
      },
      proxyStatus: networkProxyStatus,
      locality,
      warnings,
      updatedAt: Date.now(),
    };
  }

  private deviceContextSignature(context: DeviceContext): string {
    return JSON.stringify({
      fingerprint: context.fingerprint,
      location: context.location,
      locationError: context.locationError,
      network: context.network,
      networkError: context.networkError,
      attestation: {
        ...context.attestation,
        updatedAt: 0,
      },
      notes: context.notes,
    });
  }

  private emitDeviceContext(force = false): void {
    if (!this.deviceContext) return;
    const next: DeviceContext = {
      ...this.deviceContext,
      attestation: this.buildDeviceContextAttestation(),
    };
    const signature = this.deviceContextSignature(next);
    this.deviceContext = next;
    if (!force && signature === this.lastDeviceContextSignature) return;
    this.lastDeviceContextSignature = signature;
    this.events.emit({ type: 'device-context', context: next });
    this.sendToBridge('device-context', next);
    if (this.cfg.telemetryWebhookUrl) {
      this.streamWebhook('device-context', next).catch(() => {});
    }
  }

  async refreshDeviceContext(force = false): Promise<DeviceContext | null> {
    try {
      const deviceContextConfig = this.cfg.deviceContext || {};
      const context = await gatherDeviceContext(
        deviceContextConfig.requestLocationConsent ?? true,
        {
          ...deviceContextConfig,
          authBaseUrl: this.cfg.authBaseUrl,
        },
      );
      this.deviceContext = {
        ...context,
        attestation: this.buildDeviceContextAttestation(),
      };
      this.emitDeviceContext(force);
      return this.deviceContext;
    } catch (err) {
      console.warn('Failed to refresh device context:', err);
      return this.deviceContext;
    }
  }

  // ---------------------------------------------------------------------------
  // Call lifecycle controller (WhatsApp flow)
  // ---------------------------------------------------------------------------

  /** Routes inbound call-control signaling to phase changes / app events. */
  private handleControlMessage(msg: SignalMessage): void {
    const self = this.cfg.selfId;
    switch (msg.type) {
      case 'invite': {
        if (msg.to && self && msg.to !== self) return; // not for us
        if (this._callPhase !== 'idle' && this._callPhase !== 'ended') {
          console.warn(`ZeroRate RTC: Ignoring duplicate or concurrent invite from ${msg.from} in phase ${this._callPhase}`);
          return;
        }
        this.incoming = { roomId: msg.roomId, from: msg.from, displayName: msg.displayName, password: (msg as any).password };
        this.invited = true;
        this.accepted = false;
        this.ended = false;
        this.remoteId = msg.from;
        this.recomputeCallPhase(); // → ringing
        // Surface `silent` so the app can drop in without ringing (WhatsApp silent calls).
        this.events.emit({ type: 'incoming-call', roomId: msg.roomId, from: msg.from, displayName: msg.displayName, silent: (msg as any).silent });
        break;
      }
      case 'accept': {
        // Our outgoing invite was accepted; move from ringing → connecting.
        if (this.remoteId && msg.from && msg.from !== this.remoteId) return;
        if (this.accepted) return;
        this.accepted = true;
        this.clearRingTimer();
        this.recomputeCallPhase();

        // Update the transport's room accepted status and kick off the P2P connection immediately
        if (this.transport && (this.transport as any).currentRoom) {
          (this.transport as any).currentRoom.accepted = true;
          if (typeof (this.transport as any).flushPendingSignaling === 'function') {
            try {
              (this.transport as any).flushPendingSignaling(msg.from);
            } catch (err) {
              console.warn('ZeroRate RTC: Failed to flush pending signaling:', err);
            }
          }
          if (typeof (this.transport as any).getOrCreateConnection === 'function') {
            try {
              (this.transport as any).getOrCreateConnection(msg.from, (this.transport as any).currentRoom);
            } catch (err) {
              console.warn('ZeroRate RTC: Failed to trigger P2P connection immediately on accept:', err);
            }
          }
        }
        break;
      }
      case 'decline': {
        if (msg.to && self && msg.to !== self) return;
        this.clearRingTimer();
        this.setEnded('declined');
        void this.leave();
        break;
      }
      case 'cancel': {
        if (msg.to && self && msg.to !== self) return;
        // Caller bailed before we picked up.
        if (this.invited && !this.accepted) {
          this.incoming = undefined;
          this.setEnded('cancelled');
          void this.leave();
        }
        break;
      }
      case 'hangup': {
        if (msg.to && self && msg.to !== self) return;
        this.setEnded('remote-hangup');
        void this.leave();
        break;
      }
      case 'reaction': {
        if (msg.to && self && msg.to !== self) return;
        if (msg.from === self) return; // don't echo our own
        if (!isAllowedReaction(msg.emoji)) return;
        const at = Date.now();
        if (!this.reactionThrottle.allow(msg.from, at)) return; // drop a flood from one sender
        this.events.emit({ type: 'reaction-received', from: msg.from, emoji: msg.emoji, at });
        break;
      }
      case 'raise-hand': {
        if (msg.to && self && msg.to !== self) return;
        const roster = this.handRoster.set(msg.from, !!msg.raised, Date.now());
        this.events.emit({ type: 'hand-raised-changed', participantId: msg.from, raised: !!msg.raised, raisedHands: roster });
        break;
      }
      case 'identity': {
        if (msg.to && self && msg.to !== self) return;
        if (msg.from === self || !(msg as any).publicKey) return;
        void this.handlePeerIdentity(msg.from, (msg as any).publicKey);
        break;
      }
      default:
        break; // offer/answer/candidate are the transport's concern
    }
  }

  /** Recompute the folded call phase and emit when it changes. */
  private recomputeCallPhase(): void {
    const phase = deriveCallPhase({
      invited: this.invited,
      accepted: this.accepted,
      ended: this.ended,
      peerIceStates: [...this.peerIce.values()],
    });
    if (phase === this._callPhase) return;
    this._callPhase = phase;
    this.events.emit({ type: 'call-phase-changed', phase, reason: this.ended ? this._endReason : undefined });
  }

  /** Clear all per-call state before starting/answering a new call. */
  private resetCallState(): void {
    this.clearRingTimer();
    this.invited = false;
    this.accepted = false;
    this.ended = false;
    this._endReason = undefined;
    this.remoteId = undefined;
    this.peerIce.clear();
  }

  /** Mark the call ended with a reason and surface the terminal phase. */
  private setEnded(reason: CallEndReason): void {
    if (this.ended) return;
    this.ended = true;
    this._endReason = reason;
    this.clearRingTimer();
    this.recomputeCallPhase();
  }

  private startRingTimer(roomId: string, to: string): void {
    this.clearRingTimer();
    const ms = this.cfg.ringTimeoutMs ?? 45000;
    this.ringTimer = setTimeout(() => {
      // No pickup and no media negotiation began → give up.
      if (this._callPhase === 'ringing') {
        try {
          this.cfg.signaling.send({ type: 'cancel', roomId, to, from: this.cfg.selfId ?? 'me' });
        } catch {
          /* best effort */
        }
        this.setEnded('no-answer');
        void this.leave();
      }
    }, ms);
  }

  private clearRingTimer(): void {
    if (this.ringTimer) {
      clearTimeout(this.ringTimer);
      this.ringTimer = undefined;
    }
  }

  /**
   * Re-mint TURN credentials (server-address → /v1/auth/token → /v1/credentials) and
   * hot-swap them onto the live transport. Triggered when the keep-alive monitor reports
   * `credentialsValid: false`. Guarded so overlapping liveness ticks can't stampede the
   * auth service. This is what keeps a long VoIP session resilient across credential TTLs.
   */
  private async refreshTurnCredentials(proactive = false): Promise<void> {
    if (this.turnRefreshing) return;
    if (!this.cfg.authBaseUrl || !this.cfg.publicAppKey) return; // no dynamic-creds source configured
    this.turnRefreshing = true;
    try {
      console.warn(
        proactive
          ? 'ZeroRate RTC: rotating TURN credentials ahead of expiry — seamless hot-swap (no ICE restart).'
          : 'ZeroRate RTC: TURN credentials expired — re-minting and restarting ICE to recover.',
      );
      const ice = await this.fetchCoturnCredentials();
      if (ice && ice.iceServers.length > 0) {
        this.cfg.ice = ice;
        // Proactive rotation swaps creds without a restart (seamless); reactive recovery restarts.
        await this.transport?.updateIceConfig?.(ice, !proactive);
        this.events.emit({ type: 'turn-credentials-refreshed' });
      }
    } catch (err) {
      console.error('ZeroRate RTC: TURN credential refresh failed:', err);
    } finally {
      this.turnRefreshing = false;
    }
  }

  private async fetchCoturnCredentials(): Promise<IceConfig | null> {
    const authBaseUrl = this.cfg.authBaseUrl;
    if (!authBaseUrl) return null;

    const regionCode = this.cfg.regionCode || 'NG';
    const serviceCategory = this.cfg.serviceCategory || 'coturn';
    const publicAppKey = this.cfg.publicAppKey;
    const selfId = this.cfg.selfId || 'test-user';

    const mapToIceServers = (data: any) => {
      const { username, password, uris } = data;
      if (!username || !password || !uris || uris.length === 0) {
        throw new Error('Invalid credentials payload returned');
      }
      const servers: any[] = [{ urls: uris, username, credential: password }];
      // Add a STUN entry from the TURN host so direct-path srflx candidates are gathered — this
      // enables a DIRECT UDP peer path instead of always relaying — then order everything UDP-first
      // (STUN → TURN/UDP → TURN/TCP → TURNS/TLS) so the media path prefers UDP over the slow TCP relay.
      const stun = stunFromTurnUris(uris);
      if (stun) servers.unshift({ urls: stun });
      return orderIceServersUdpFirst(servers);
    };

    // The /v1/credentials response carries `expires` (ISO) / `ttl` (s). Capture an absolute
    // epoch-ms expiry so the keep-alive monitor can rotate the credentials BEFORE they lapse.
    const expiryMs = (data: any): number | undefined => {
      if (data?.expires) { const t = Date.parse(data.expires); if (!Number.isNaN(t)) return t; }
      if (typeof data?.ttl === 'number') return Date.now() + data.ttl * 1000;
      return undefined;
    };

    // Path A: Try same-origin Netlify proxy (to bypass browser CORS)
    if (typeof window !== 'undefined') {
      try {
        const signalingBase = window.location.origin;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (this.cfg.token) headers.Authorization = `Bearer ${this.cfg.token}`;
        const res = await fetch(`${signalingBase}/api/signaling/turn-credentials`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            authBaseUrl,
            publicAppKey,
            regionCode,
            selfId
          })
        });
        if (res.ok) {
          const credsData = await res.json();
          console.log('ZeroRate RTC: Successfully fetched TURN credentials via same-origin signaling proxy.');
          return {
            iceServers: mapToIceServers(credsData),
            turnApiBase: credsData.turnApiBase,
            turnToken: credsData.turnToken,
            turnUsername: credsData.username,
            turnExpiresAt: expiryMs(credsData)
          };
        }
      } catch (e: any) {
        console.warn('ZeroRate RTC: Same-origin signaling proxy credentials request failed, trying direct path:', e.message || e);
      }
    }

    // Path B: Try direct fetch
    try {
      // 1. Fetch server address
      const serverAddressUrl = `${authBaseUrl}/api/authsvc/v1/region/server-address?region-code=${regionCode}&service=${serviceCategory}`;
      console.log(`ZeroRate RTC: Resolving TURN server address from: ${serverAddressUrl}`);
      const headers: Record<string, string> = {};
      if (publicAppKey) {
        headers['x-header-key'] = publicAppKey;
      }
      const serverAddressRes = await fetch(serverAddressUrl, { headers });
      if (!serverAddressRes.ok) {
        throw new Error(`Server address endpoint returned status ${serverAddressRes.status}`);
      }
      const serverAddressData = await serverAddressRes.json();
      const turnApiBase = serverAddressData.data?.server_address || serverAddressData.serverAddress;
      if (!turnApiBase) {
        throw new Error('No serverAddress returned from server-address endpoint');
      }

      // 2. Fetch auth token from Coturn service
      const tokenUrl = `${turnApiBase}/v1/auth/token`;
      console.log(`ZeroRate RTC: Authenticating with Coturn service at: ${tokenUrl}`);
      const tokenRes = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          public_app_key: publicAppKey,
          subject: selfId === 'local-peer' ? 'admin' : selfId,
          ttl_seconds: 86400
        })
      });
      if (!tokenRes.ok) {
        throw new Error(`Token endpoint returned status ${tokenRes.status}`);
      }
      const tokenData = await tokenRes.json();
      const token = tokenData.token;
      if (!token) {
        throw new Error('No token returned from auth/token endpoint');
      }

      // 3. Fetch credentials
      const credentialsUrl = `${turnApiBase}/v1/credentials`;
      console.log(`ZeroRate RTC: Fetching TURN credentials from: ${credentialsUrl}`);
      const credentialsRes = await fetch(credentialsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          identity: selfId,
          ttl_seconds: 3600,
          uris: []
        })
      });
      if (!credentialsRes.ok) {
        throw new Error(`Credentials endpoint returned status ${credentialsRes.status}`);
      }
      const credentialsData = await credentialsRes.json();
      const iceServers = mapToIceServers(credentialsData);
      console.log('ZeroRate RTC: Successfully loaded direct TURN credentials.');
      return {
        iceServers,
        turnApiBase,
        turnToken: token,
        turnUsername: credentialsData.username,
        turnExpiresAt: expiryMs(credentialsData)
      };
    } catch (err: any) {
      console.warn(`ZeroRate RTC: Direct Coturn fetch failed (possibly due to CORS). Falling back to Serverless Proxy:`, err);

      // Path C: Fallback to Serverless Proxy endpoint
      try {
        const proxyUrl = `${authBaseUrl}/api/authsvc/v1/credentials`;
        console.log(`ZeroRate RTC: Requesting credentials via proxy at: ${proxyUrl}`);
        const proxyRes = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            public_app_key: publicAppKey,
            identity: selfId,
            region_code: regionCode,
            service: serviceCategory
          })
        });

        if (!proxyRes.ok) {
          throw new Error(`Serverless proxy returned status ${proxyRes.status}`);
        }

        const credentialsData = await proxyRes.json();
        const iceServers = mapToIceServers(credentialsData);
        console.log('ZeroRate RTC: Successfully resolved TURN credentials via Serverless Proxy.');
        return {
          iceServers,
          turnApiBase: credentialsData.turnApiBase || 'https://turns-stg.soa.africa',
          turnToken: credentialsData.turnToken,
          turnUsername: credentialsData.username,
          turnExpiresAt: expiryMs(credentialsData)
        };
      } catch (proxyErr) {
        console.error('ZeroRate RTC: Both direct and proxy Coturn credentials fetches failed:', proxyErr);
        return null;
      }
    }
  }

  private sendToBridge(type: string, payload: any): void {
    const payloadStr = JSON.stringify({
      source: 'udm-web',
      type,
      payload,
      timestamp: new Date().toISOString()
    });
    
    // React Native WebView
    if (typeof window !== 'undefined' && (window as any).ReactNativeWebView && typeof (window as any).ReactNativeWebView.postMessage === 'function') {
      (window as any).ReactNativeWebView.postMessage(payloadStr);
    }
    // iOS WKWebView message handlers
    if (typeof window !== 'undefined' && (window as any).webkit?.messageHandlers) {
      if ((window as any).webkit.messageHandlers.EdgeBlasterRTC) {
        (window as any).webkit.messageHandlers.EdgeBlasterRTC.postMessage(payloadStr);
      }
      if ((window as any).webkit.messageHandlers.ZeroRateRTC) {
        (window as any).webkit.messageHandlers.ZeroRateRTC.postMessage(payloadStr);
      }
    }
    // Android WebView Javascript Interface
    if (typeof window !== 'undefined') {
      if ((window as any).EdgeBlasterRTCAndroid && typeof (window as any).EdgeBlasterRTCAndroid.postMessage === 'function') {
        (window as any).EdgeBlasterRTCAndroid.postMessage(payloadStr);
      }
      if ((window as any).ZeroRateRTCAndroid && typeof (window as any).ZeroRateRTCAndroid.postMessage === 'function') {
        (window as any).ZeroRateRTCAndroid.postMessage(payloadStr);
      }
    }
    // Flutter WebView JavaScript Channel
    if (typeof window !== 'undefined') {
      if ((window as any).EdgeBlasterRTCFlutter && typeof (window as any).EdgeBlasterRTCFlutter.postMessage === 'function') {
        (window as any).EdgeBlasterRTCFlutter.postMessage(payloadStr);
      }
      if ((window as any).ZeroRateRTCFlutter && typeof (window as any).ZeroRateRTCFlutter.postMessage === 'function') {
        (window as any).ZeroRateRTCFlutter.postMessage(payloadStr);
      }
    }
  }

  private async streamWebhook(type: string, payload: any): Promise<void> {
    const url = this.cfg.telemetryWebhookUrl;
    if (!url) return;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.cfg.telemetryWebhookSecret || ''}`,
          'x-telemetry-secret': this.cfg.telemetryWebhookSecret || ''
        },
        body: JSON.stringify({
          event: type,
          payload,
          timestamp: Date.now()
        })
      });
      this.events.emit({
        type: 'webhook-success' as any,
        webhookUrl: url,
        status: res.status,
        event: type
      } as any);
    } catch (e: any) {
      this.events.emit({
        type: 'webhook-failed' as any,
        webhookUrl: url,
        event: type,
        error: e.message || String(e)
      } as any);
    }
  }

  /** Expose a native injection override for mobile bridges */
  setNativeDeviceContext(context: DeviceContext): void {
    const normalized: DeviceContext = {
      ...context,
      capturedAt: context.capturedAt || Date.now(),
      attestation: this.buildDeviceContextAttestation(),
      notes: uniqueStrings([
        ...(this.deviceContext?.notes || []),
        ...(context.notes || []),
      ]),
    };
    this.deviceContext = normalized;
    this.emitDeviceContext(true);
  }

  /**
   * The Signaling Infrastructure Provider in effect for the active session — the
   * pinned `cfg.provider`, or the value inferred at `join()` from room size/mode.
   * `undefined` before the first join.
   */
  get provider(): SignalingProvider | undefined {
    return this._provider ?? this.cfg.provider;
  }

  // ---------------------------------------------------------------------------
  // Product plan (Connectra / Connectra+) + role + Online Presence Directory.
  // ---------------------------------------------------------------------------

  /** The resolved product profile (provider, cap, role capabilities) for this session. */
  get productProfile(): ProductProfile {
    return resolveProductProfile({ billingMode: this.cfg.billingMode, peerRole: this.cfg.peerRole, provider: this.provider });
  }
  get billingMode(): BillingMode {
    return this.cfg.billingMode ?? 'connectra_basic';
  }
  /** Connectra+ role of THIS peer (undefined on Basic). */
  get peerRole(): PeerRole | undefined {
    return this.cfg.billingMode === 'connectra_plus' ? (this.cfg.peerRole ?? 'client') : undefined;
  }
  get availability(): AvailabilityStatus {
    return this._availability;
  }

  /** The Online Presence Directory: live roster + online/e2ee/turns/role/availability per peer. */
  get onlinePeers(): DirectoryPeer[] {
    return this.directory.snapshot();
  }

  /**
   * Feed the directory from an external presence source (e.g. the /api/signaling/presence poll, which
   * carries cross-room presence). Rows are enriched with the session's live e2ee status; per-peer
   * `turns_status`/`peerRole`/`availability_status` are taken from the entry when present. Emits
   * `presence-changed`. `self` is excluded (the app renders "you" separately).
   */
  ingestPresence(entries: Array<DirectoryPeerUpdate & { self?: boolean }>, now = Date.now()): DirectoryPeer[] {
    const e2ee = this._e2eeActive ? 'active' : (this.cfg.e2ee ? 'inactive' : 'unknown');
    for (const e of entries) {
      if (e.self || e.peerId === this.cfg.selfId) continue;
      this.directory.upsert({ ...e, e2ee_status: e.e2ee_status ?? (e2ee as any) }, now);
    }
    this.directory.prune(now);
    const peers = this.directory.snapshot();
    this.events.emit({ type: 'presence-changed', peers });
    return peers;
  }

  /**
   * Connectra+ operators/admins: set your availability (active | busy | offline). Surfaces in every
   * peer's directory and is what a client sees before calling. The AUTHORITATIVE availability + queue
   * live on the edge media backend; this also POSTs to the presence plane so peers see it in real time.
   */
  setAvailability(status: AvailabilityStatus): void {
    this._availability = status;
    const self = this.cfg.selfId ?? 'me';
    this.directory.upsert({ peerId: self, availability_status: status, peerRole: this.peerRole, online_status: status === 'offline' ? 'offline' : 'online' }, Date.now());
    this.events.emit({ type: 'presence-changed', peers: this.directory.snapshot() });
  }

  /**
   * The resolved native call-UI preference for the current session — feed straight into
   * `mountCallUI(client, client.ui)` (@zr/adapter-web) or a native renderer. `auto` collapses
   * to 'whatsapp' for a 1:1 `call` and 'google-meet' for a mesh room / managed conference.
   */
  get ui(): { style: CallUxStyle; glass: boolean; mode?: CallMode; maxParticipants?: number } {
    const style = resolveCallUx({ style: this.cfg.ui?.style, mode: this._roomMode, maxParticipants: this._roomMax });
    return { style, glass: this.cfg.ui?.glass !== false, mode: this._roomMode, maxParticipants: this._roomMax };
  }

  /** The local MediaStream being published (whether supplied or acquired internally). */
  get localStream(): MediaStream | undefined {
    return this._localStream;
  }

  /** Supply a pre-acquired stream before join() so app-side mute/camera reaches peers. */
  provideLocalStream(stream: MediaStream): void {
    this._localStream = stream;
  }

  /** Enable/disable the outgoing microphone (mutes what peers receive). */
  setMicEnabled(on: boolean): void {
    this._localStream?.getAudioTracks().forEach((t) => (t.enabled = on));
    this.events.emit({
      type: 'track-subscribed', // mock/trigger state update notifications for controllers
      participantId: this.cfg.selfId || 'local-peer',
      track: { id: 'mic', kind: 'audio', muted: !on }
    } as any);
  }

  /** Enable/disable the outgoing camera (peers stop receiving video frames). */
  setCameraEnabled(on: boolean): void {
    this._localStream?.getVideoTracks().forEach((t) => (t.enabled = on));
    this.events.emit({
      type: 'track-subscribed',
      participantId: this.cfg.selfId || 'local-peer',
      track: { id: 'cam', kind: 'video', muted: !on }
    } as any);
  }

  /** Swap the outgoing video track on all peers (screen share / camera switch). */
  async replaceVideoTrack(track: MediaStreamTrack | null): Promise<void> {
    await this.transport?.replaceVideoTrack?.(track);
  }

  /** True while a screen share is active. */
  get isScreenSharing(): boolean {
    return !!this.screenShare;
  }

  /**
   * The stream the local presenter should preview: the composite (screen + circular
   * self-cam) when overlay compositing is active, otherwise the raw screen, otherwise
   * the camera.
   */
  get displayStream(): MediaStream | undefined {
    return (
      this.screenShare?.composite?.stream ??
      this.screenShare?.screenStream ??
      this.effectPipeline?.previewStream ??
      this._localStream
    );
  }

  /**
   * Start sharing the screen. With `selfOverlay` (default true) on a platform that
   * supports compositing, the presenter's camera is drawn as a circular bubble onto
   * the shared screen and that single combined stream is published to every peer
   * (StreamYard-style). Falls back to publishing the raw screen track otherwise.
   */
  async startScreenShare(
    opts: { selfOverlay?: boolean; overlay?: ScreenCompositeOptions } = {},
  ): Promise<void> {
    if (this.screenShare) return;
    const platform = this.cfg.platform;
    if (!platform.getDisplayMedia) {
      throw new Error('Screen share is not supported by this platform adapter (no getDisplayMedia).');
    }

    const screenStream = await platform.getDisplayMedia();
    const screenTrack = screenStream.getVideoTracks()[0];
    const cameraTrack = this._localStream?.getVideoTracks()[0] ?? null;

    let outgoing: MediaStreamTrack = screenTrack;
    let composite: ScreenComposite | undefined;

    if (opts.selfOverlay !== false && cameraTrack && platform.createScreenComposite) {
      composite = platform.createScreenComposite(screenStream, this._localStream ?? null, opts.overlay);
      outgoing = composite.stream.getVideoTracks()[0] ?? screenTrack;
    }

    await this.transport?.replaceVideoTrack?.(outgoing);

    // Auto-stop when the user ends sharing via the browser/OS picker UI.
    if (screenTrack) {
      screenTrack.onended = () => {
        void this.stopScreenShare();
      };
    }

    this.screenShare = { screenStream, composite, cameraTrack };
    this.events.emit({ type: 'screen-share-changed', active: true });
  }

  /** Stop screen sharing and restore the presenter's camera to all peers. */
  async stopScreenShare(): Promise<void> {
    const s = this.screenShare;
    if (!s) return;
    this.screenShare = undefined;

    try {
      await this.transport?.replaceVideoTrack?.(s.cameraTrack);
    } catch {
      /* ignore restore errors */
    }
    s.composite?.stop();
    s.screenStream.getTracks().forEach((t) => t.stop());

    this.events.emit({ type: 'screen-share-changed', active: false });
  }

  /** The current call lifecycle phase (idle/ringing/connecting/active/reconnecting/ended). */
  get callPhase(): CallPhase {
    return this._callPhase;
  }

  /** Details of an unanswered incoming call, if one is ringing. */
  get incomingCall(): { roomId: string; from: string; displayName?: string; password?: string } | undefined {
    return this.incoming;
  }

  /**
   * Start a 1:1 call: opens a 2-person P2P room and rings the callee (WhatsApp flow).
   * The call stays in `ringing` until the callee accepts (or media negotiation begins),
   * then transitions to `connecting` → `active`. Times out as `no-answer`.
   */
  async start(opts: { to: string; roomId: string; password?: string; silent?: boolean }): Promise<void> {
    this.resetCallState();
    this.remoteId = opts.to;
    this.invited = true;
    this.recomputeCallPhase(); // → ringing

    await this.establish({ roomId: opts.roomId, mode: 'call', max: 2, password: opts.password });

    // Ring over the signaling plane (in-room) and via the push channel (wake a
    // backgrounded device). `silent` lets the callee drop in without a ringtone
    // (WhatsApp silent calls). Best-effort: a failed push must not abort the call.
    this.cfg.signaling.send({ type: 'invite', roomId: opts.roomId, from: this.cfg.selfId ?? 'me', to: opts.to, password: opts.password, silent: opts.silent } as any);
    await this.cfg.invites
      ?.sendInvite({ roomId: opts.roomId, from: this.cfg.selfId ?? 'me', to: opts.to })
      .catch(() => {});

    this.startRingTimer(opts.roomId, opts.to);
  }

  /**
   * Answer the current incoming call (or an explicit one). Sends `accept`, joins the
   * room, and moves the phase to `connecting`.
   */
  async accept(opts?: { roomId?: string; from?: string; password?: string }): Promise<void> {
    const roomId = opts?.roomId ?? this.incoming?.roomId;
    const from = opts?.from ?? this.incoming?.from;
    const password = opts?.password ?? (this.incoming as any)?.password;
    if (!roomId) throw new Error('accept(): there is no incoming call to accept.');

    this.resetCallState();
    this.remoteId = from;
    this.accepted = true;
    this.incoming = undefined;
    this.recomputeCallPhase(); // → connecting

    await this.establish({ roomId, mode: 'call', max: 2, accepted: true, password });
    
    // Flush pending signaling messages first (handles offers that arrived slightly before accept resolved)
    if (from && this.transport && typeof (this.transport as any).flushPendingSignaling === 'function') {
      try {
        await (this.transport as any).flushPendingSignaling(from);
      } catch (err) {
        console.warn('ZeroRate RTC: Failed to flush pending signaling on accept:', err);
      }
    }

    // Kick off connection immediately for the accepted peer (eliminates 3s poll delay)
    if (from && this.transport && typeof (this.transport as any).getOrCreateConnection === 'function') {
      try {
        (this.transport as any).getOrCreateConnection(from, (this.transport as any).currentRoom);
      } catch (err) {
        console.warn('ZeroRate RTC: Failed to trigger P2P connection immediately on accept:', err);
      }
    }

    this.cfg.signaling.send({ type: 'accept', roomId, from: this.cfg.selfId ?? 'me', to: from });
  }

  /** Reject the current incoming call (or an explicit one). */
  async decline(opts?: { roomId?: string; from?: string; password?: string }): Promise<void> {
    const roomId = opts?.roomId ?? this.incoming?.roomId;
    const from = opts?.from ?? this.incoming?.from;
    const password = opts?.password ?? (this.incoming as any)?.password;
    if (password && 'password' in this.cfg.signaling) {
      (this.cfg.signaling as any).password = password;
    }
    if (roomId && from) {
      try {
        this.cfg.signaling.send({ type: 'decline', roomId, to: from, from: this.cfg.selfId ?? 'me' });
      } catch {
        /* best effort */
      }
    }
    this.incoming = undefined;
    this.setEnded('declined');
    await this.leave();
  }

  /** Cancel an outgoing call before it is accepted. */
  async cancel(): Promise<void> {
    this.clearRingTimer();
    if (this.remoteId && this.currentRoomId) {
      try {
        this.cfg.signaling.send({ type: 'cancel', roomId: this.currentRoomId, to: this.remoteId, from: this.cfg.selfId ?? 'me' });
      } catch {
        /* best effort */
      }
    }
    this.incoming = undefined;
    this.setEnded('cancelled');
    await this.leave();
  }

  /** End the active call: tell the peer(s), tear down, and surface `ended`. */
  async hangup(): Promise<void> {
    if (this.remoteId && this.currentRoomId) {
      try {
        this.cfg.signaling.send({ type: 'hangup', roomId: this.currentRoomId, to: this.remoteId, from: this.cfg.selfId ?? 'me' });
      } catch {
        /* best effort */
      }
    }
    this.setEnded('hangup');
    await this.leave();
  }

  /**
   * Join (or create) a room/lobby — the group path, with no ringing handshake (you are
   * committing to the session directly). PathSelector picks P2P vs SFU from mode + size.
   */
  async join(opts: JoinOptions): Promise<void> {
    this.resetCallState();
    this.accepted = true; // group join commits immediately; no incoming-call gate
    this.recomputeCallPhase();
    await this.establish(opts);
  }

  /** Connect signaling + media transport for a room. Shared by start/accept/join. */
  private async establish(opts: JoinOptions): Promise<void> {
    this.currentRoomId = opts.roomId;
    this._roomMode = opts.mode ?? 'group';
    this._roomMax = opts.max;
    if (opts.password && 'password' in (this.cfg.signaling as any)) {
      (this.cfg.signaling as any).password = opts.password;
    }

    if (this.cfg.authBaseUrl && this.cfg.publicAppKey) {
      // Preserve any developer-configured STUN servers (e.g. Google STUN) BEFORE we swap in the
      // dynamic TURN config — dropping them removes direct-path srflx and forces a relay.
      const configuredStun = (this.cfg.ice?.iceServers || []).filter((s) => {
        const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
        return !s.username && urls.some((u) => String(u).toLowerCase().startsWith('stun:'));
      });
      const dynamicIceConfig = await this.fetchCoturnCredentials();
      if (dynamicIceConfig && dynamicIceConfig.iceServers.length > 0) {
        // Merge configured STUN + dynamic (STUN + TURN) and re-order UDP-first so the media path
        // prefers a direct/UDP-relay route over the slow TLS/TCP relay.
        const merged = orderIceServersUdpFirst([...configuredStun, ...dynamicIceConfig.iceServers]);
        this.cfg.ice = { ...dynamicIceConfig, iceServers: merged };
        console.log('ZeroRate RTC: Configured client with UDP-preferred dynamic coturn ICE config:', this.cfg.ice);
      }
    }

    if (!this.unsubscribeSignaling) {
      this.unsubscribeSignaling = this.cfg.signaling.on((msg) => this.handleControlMessage(msg));
    }

    // Stand up the E2E encryption context once (P2P path). It engages only on runtimes with
    // Insertable Streams; elsewhere it's a transparent no-op and `e2eeActive` stays false.
    if (this.cfg.e2ee?.secret && !this.e2ee) {
      if (isE2eeSupported()) {
        this.e2ee = new E2eeContext(this.cfg.e2ee.secret, this.cfg.selfId ?? 'me');
        this._e2eeActive = true;
      } else {
        console.warn('ZeroRate RTC: E2E encryption requested but Insertable Streams are unavailable here; calls are still DTLS-encrypted in transit but not end-to-end.');
        this._e2eeActive = false;
      }
      this.emitDeviceContext();
    }

    await this.cfg.signaling.connect(this.cfg.token);

    const room: Room = {
      id: opts.roomId,
      mode: opts.mode ?? 'group',
      maxParticipants: opts.max ?? 10,
      state: 'connecting',
      participants: [],
      accepted: opts.accepted ?? this.accepted,
    };

    const deps: TransportDeps = {
      platform: this.cfg.platform,
      signaling: this.cfg.signaling,
      ice: this.cfg.ice,
      provider: this.cfg.provider,
      livekitUrl: this.cfg.livekitUrl,
      whipEndpoint: this.cfg.whipEndpoint,
      whepEndpoint: this.cfg.whepEndpoint,
      mediaToken: this.cfg.mediaToken,
      token: this.cfg.token,
      selfId: this.cfg.selfId,
      keepAliveMs: this.cfg.keepAliveMs,
      e2ee: this.e2ee,
      iceTransportPolicy: this.cfg.iceTransportPolicy,
      events: this.events,
      fetch: async (url: string, options: any) => {
        if (url.endsWith('/v1/peers/ping')) {
          const authBaseUrl = this.cfg.authBaseUrl || window.location.origin;
          const proxyUrl = `${authBaseUrl}/api/signaling/turn-ping`;
          
          let username = '';
          try {
            const body = JSON.parse(options.body);
            username = body.username;
          } catch {}

          const token = options.headers?.Authorization?.replace('Bearer ', '') || '';
          const peerHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
          if (this.cfg.token) peerHeaders.Authorization = `Bearer ${this.cfg.token}`;

          const res = await fetch(proxyUrl, {
            method: 'POST',
            headers: peerHeaders,
            body: JSON.stringify({
              turnApiBase: url.replace('/v1/peers/ping', ''),
              turnToken: token,
              turnUsername: username
            })
          });

          if (!res.ok) {
            return new Response(JSON.stringify({ error: 'Proxy ping failed' }), { status: res.status });
          }

          const rawData = await res.json();
          return new Response(JSON.stringify({
            alive: rawData.alive,
            turn_reachable: rawData.turn_reachable,
            credentials_valid: rawData.credentials_valid,
            credentials_expires_at: rawData.credentials_expires_at
          }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          });
        }
        return fetch(url, options);
      }
    };

    // Close any prior transport (e.g. accepting after a lobby join) to avoid leaking it.
    if (this.transport) {
      await this.transport.close(false);
      this.transport = undefined;
    }

    this.transport = selectTransport(room, deps);
    // Reuse a supplied/preview stream so there's a single getUserMedia and UI mute
    // toggles on it actually affect what peers receive.
    const media = this._localStream ?? (await this.cfg.platform.getUserMedia({ audio: true, video: true }));
    this._localStream = media;
    await this.transport.connect(room);
    await this.transport.publish(media);
    this.events.emit({ type: 'state-changed', state: 'connected' });
    this.emitDeviceContext(true);

    // Announce our public-key identity so peers can verify us (fingerprint + safety number).
    // Best-effort and async — it never blocks media coming up.
    void this.announceIdentity();

    // Start the session-scoped raw link-capacity probe (fast.com-style; runs only while active).
    this.startLinkCapacityProbe();
  }

  get remoteStream(): MediaStream | undefined {
    return this.transport?.remoteStream;
  }

  /** The received stream for a specific participant (per-participant model). */
  getParticipantStream(participantId: string): MediaStream | undefined {
    return this.transport?.getParticipantStream?.(participantId);
  }

  /** Resolve the speed-test endpoint (config override → same-origin default). */
  private resolveSpeedTestUrl(): string | undefined {
    if (this.cfg.linkCapacity?.url) return this.cfg.linkCapacity.url;
    if (typeof window !== 'undefined') return `${window.location.origin}/api/signaling/speedtest`;
    return undefined;
  }

  /**
   * Measure the RAW link capacity (fast.com-style) once: download then upload an incompressible
   * payload and time it, returning down/up Mbps + probe latency. Emits a `link-capacity` event.
   * This is the true "how fast is this link" figure, distinct from the call's media throughput.
   */
  async measureLinkCapacity(): Promise<LinkCapacitySample | undefined> {
    if (this.linkCapacityProbing) return this.lastLinkCapacity;
    const url = this.resolveSpeedTestUrl();
    const fetchImpl: typeof fetch | undefined = (this.cfg as any).fetch ?? (typeof fetch !== 'undefined' ? fetch : undefined);
    if (!url || !fetchImpl) return undefined;
    this.linkCapacityProbing = true;
    const authHeaders: Record<string, string> = this.cfg.token ? { Authorization: `Bearer ${this.cfg.token}` } : {};
    const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    try {
      // ---- Download: time from the FIRST byte to the last (excludes TTFB/RTT) ----
      const dlBytes = this.cfg.linkCapacity?.downloadBytes ?? 3_000_000;
      const reqStart = nowMs();
      const res = await fetchImpl(`${url}?bytes=${dlBytes}&t=${Date.now()}`, { headers: authHeaders, cache: 'no-store' as any });
      let received = 0;
      let firstByteAt = 0;
      const reader = (res.body as any)?.getReader?.();
      if (reader) {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          if (!firstByteAt) firstByteAt = nowMs();
          received += value?.length || 0;
        }
      } else {
        const buf = await res.arrayBuffer();
        firstByteAt = reqStart;
        received = buf.byteLength;
      }
      const latencyMs = Math.max(0, Math.round((firstByteAt || reqStart) - reqStart));
      const downMbps = throughputMbps(received, nowMs() - (firstByteAt || reqStart));

      // ---- Upload: time an incompressible payload POST ----
      const ulBytes = this.cfg.linkCapacity?.uploadBytes ?? 1_000_000;
      let upMbps = 0;
      try {
        const payload = new Uint8Array(ulBytes);
        const u0 = nowMs();
        await fetchImpl(url, { method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/octet-stream' }, body: payload });
        upMbps = throughputMbps(ulBytes, nowMs() - u0);
      } catch { /* upload optional */ }

      const smoothed = this.linkCapacityTracker.update({ downMbps, upMbps });
      const sample: LinkCapacitySample = { downMbps: smoothed.downMbps, upMbps: smoothed.upMbps, latencyMs, at: Date.now(), endpoint: url };
      this.lastLinkCapacity = sample;
      this.events.emit({ type: 'link-capacity', ...sample });
      return sample;
    } catch (err) {
      console.warn('ZeroRate RTC: link-capacity probe failed:', err);
      return this.lastLinkCapacity;
    } finally {
      this.linkCapacityProbing = false;
    }
  }

  /** The most recent raw link-capacity sample, if any. */
  get linkCapacity(): LinkCapacitySample | undefined {
    return this.lastLinkCapacity;
  }

  /** Start the session-scoped capacity probe loop (first probe immediately, then every intervalMs). */
  private startLinkCapacityProbe(): void {
    if (this.cfg.linkCapacity?.enabled === false || this.linkCapacityTimer) return;
    if (!this.resolveSpeedTestUrl()) return;
    const intervalMs = Math.max(10_000, this.cfg.linkCapacity?.intervalMs ?? 30_000);
    // First probe shortly after connect (let media settle), then on the interval.
    setTimeout(() => { void this.measureLinkCapacity(); }, 2500);
    this.linkCapacityTimer = setInterval(() => { void this.measureLinkCapacity(); }, intervalMs);
  }

  private stopLinkCapacityProbe(): void {
    if (this.linkCapacityTimer) { clearInterval(this.linkCapacityTimer); this.linkCapacityTimer = undefined; }
    this.linkCapacityTracker = new LinkCapacityTracker();
    this.lastLinkCapacity = undefined;
  }

  async leave(): Promise<void> {
    // If we got here without an explicit reason (e.g. a direct leave()), treat it as a
    // local hang-up. Guarded, so an already-set decline/no-answer reason is preserved.
    this.setEnded('hangup');
    this.stopLinkCapacityProbe();
    if (this.unsubscribeSignaling) {
      this.unsubscribeSignaling();
      this.unsubscribeSignaling = undefined;
    }
    await this.transport?.close(true);
    this.transport = undefined;
    this.effectPipeline?.stop();
    this.effectPipeline = undefined;
    this._rawCameraTrack = undefined;
    this.handRoster.clear();
    this._handRaised = false;
    this.reactionThrottle.reset();
    this.peerPublicKeys.clear();
    this.identityAnnouncedTo.clear();
    this.peerHeartbeats.clear();
    this.directory.clear();
    this.turnLiveness = null;
    this.currentRoomId = undefined;
    this.remoteId = undefined;
    this.cfg.signaling.close();
    this.events.emit({ type: 'state-changed', state: 'ended' });
    this.emitDeviceContext(true);
  }

  /**
   * Google Meet off-screen optimization: pause a remote tile's video when it scrolls
   * off-screen / onto a second page, resume it when visible. Saves decode CPU on large
   * grids. (Pair with pausing the HTML5 <video> element in the UI for the full effect.)
   */
  setParticipantVideoActive(participantId: string, active: boolean): void {
    this.transport?.setRemoteVideoEnabled?.(participantId, active);
  }

  // ---------------------------------------------------------------------------
  // In-call interactions — reactions + raise hand (ride the signaling mailbox, so
  // they work identically on the P2P mesh and the SFU path).
  // ---------------------------------------------------------------------------

  /**
   * Fan a room-wide control message out to every known participant. The signaling mailbox
   * is peer-centric (each message targets one recipient), so a room broadcast is N sends —
   * one per participant — built by `build(to)`. Best-effort and async; never throws.
   */
  private async fanOutToRoom(build: (to: string) => SignalMessage): Promise<void> {
    const roomId = this.currentRoomId;
    if (!roomId) return;
    const self = this.cfg.selfId ?? 'me';
    let peers: string[] = [];
    try {
      peers = (await this.cfg.signaling.getParticipants?.(roomId)) ?? [];
    } catch { /* fall through to the 1:1 fallback */ }
    // 1:1 fallback: if discovery is unavailable, at least reach the known counterpart.
    if (peers.length === 0 && this.remoteId) peers = [this.remoteId];
    for (const to of peers) {
      if (to === self) continue;
      try { this.cfg.signaling.send(build(to)); } catch { /* best effort per peer */ }
    }
  }

  // ---------------------------------------------------------------------------
  // Per-participant public-key identity (P2P): announce our key, verify peers.
  // ---------------------------------------------------------------------------

  /** Our session public key (base64url), or undefined when identity is off/unsupported. */
  get identityPublicKey(): string | undefined {
    return this.identity?.publicKey;
  }

  /** The verified safety number for a peer (compare out-of-band to rule out a MITM). */
  async getPeerSafetyNumber(peerId: string): Promise<string | undefined> {
    const peerKey = this.peerPublicKeys.get(peerId);
    if (!peerKey || !this.identity) return undefined;
    return computeSafetyNumber(this.identity.publicKey, peerKey);
  }

  /** The compact fingerprint of a peer's announced public key. */
  async getPeerFingerprint(peerId: string): Promise<string | undefined> {
    const peerKey = this.peerPublicKeys.get(peerId);
    if (!peerKey) return undefined;
    return computeFingerprint(peerKey);
  }

  /** Lazily mint this session's identity keypair (once), if enabled + supported. */
  private async ensureIdentity(): Promise<void> {
    if (this.identity || this.cfg.peerIdentity === false || !isIdentitySupported()) return;
    try {
      this.identity = await generatePeerIdentity();
    } catch (err) {
      console.warn('ZeroRate RTC: could not generate peer identity:', err);
    }
  }

  /** Announce our public key to every known participant (idempotent per peer). */
  private async announceIdentity(): Promise<void> {
    await this.ensureIdentity();
    if (!this.identity) return;
    const publicKey = this.identity.publicKey;
    await this.fanOutToRoom((to) => {
      this.identityAnnouncedTo.add(to);
      return { type: 'identity', roomId: this.currentRoomId!, from: this.cfg.selfId ?? 'me', to, publicKey } as any;
    });
  }

  /** Record a peer's announced key, surface its fingerprint + safety number, reply once. */
  private async handlePeerIdentity(peerId: string, publicKey: string): Promise<void> {
    this.peerPublicKeys.set(peerId, publicKey);
    await this.ensureIdentity();
    const fp = await computeFingerprint(publicKey);
    const sn = this.identity ? await computeSafetyNumber(this.identity.publicKey, publicKey) : '';
    this.events.emit({ type: 'peer-identity', participantId: peerId, publicKey, fingerprint: fp, safetyNumber: sn });
    // Reply with our identity so a late joiner completes the exchange (once per peer).
    if (this.identity && !this.identityAnnouncedTo.has(peerId) && this.currentRoomId) {
      this.identityAnnouncedTo.add(peerId);
      try {
        this.cfg.signaling.send({ type: 'identity', roomId: this.currentRoomId, from: this.cfg.selfId ?? 'me', to: peerId, publicKey: this.identity.publicKey } as any);
      } catch { /* best effort */ }
    }
  }

  /** Send a quick emoji reaction to the room ("React on calls"). Rate-limited per sender. */
  sendReaction(emoji: ReactionEmoji | string): void {
    if (!isAllowedReaction(emoji)) {
      console.warn(`ZeroRate RTC: ignoring unsupported reaction "${emoji}".`);
      return;
    }
    const self = this.cfg.selfId ?? 'me';
    if (!this.reactionThrottle.allow(self, Date.now())) return;
    void this.fanOutToRoom((to) => ({ type: 'reaction', roomId: this.currentRoomId!, from: self, to, emoji } as any));
    // Echo locally so the sender sees their own reaction immediately.
    this.events.emit({ type: 'reaction-received', from: self, emoji, at: Date.now() });
  }

  /** Raise or lower the local hand ("let people know you have a question"). */
  raiseHand(on = true): void {
    this._handRaised = on;
    const self = this.cfg.selfId ?? 'me';
    const roster = this.handRoster.set(self, on, Date.now());
    void this.fanOutToRoom((to) => ({ type: 'raise-hand', roomId: this.currentRoomId!, from: self, to, raised: on } as any));
    this.events.emit({ type: 'hand-raised-changed', participantId: self, raised: on, raisedHands: roster });
  }

  /** True while the local participant's hand is raised. */
  get isHandRaised(): boolean {
    return this._handRaised;
  }

  /** The raised-hand roster, ordered longest-waiting first (host can "call on" people). */
  get raisedHands(): RaisedHand[] {
    return this.handRoster.snapshot();
  }

  // ---------------------------------------------------------------------------
  // Video effects — camera filters & background ("Add a filter or background").
  // ---------------------------------------------------------------------------

  /** The currently applied camera effect. */
  get videoEffect(): VideoEffect {
    return this._videoEffect;
  }

  /**
   * Apply a camera effect (blur/filters/background). Processes the local camera track via
   * the platform's effect pipeline and publishes the processed track to every peer, so
   * remote participants see the effect too. Pass `{ type: 'none' }` to restore the raw camera.
   */
  async setVideoEffect(effect: VideoEffect | VideoEffectType): Promise<void> {
    const next = normalizeEffect(effect);
    this._videoEffect = next;

    const sourceTrack = this._rawCameraTrack ?? this._localStream?.getVideoTracks()[0] ?? null;
    if (!sourceTrack) return; // audio-only call: nothing to process

    // Tear down any prior pipeline first.
    this.effectPipeline?.stop();
    this.effectPipeline = undefined;

    if (isNoOpEffect(next) || !this.cfg.platform.createVideoEffectPipeline) {
      // Restore the raw camera track to peers (and drop a pipeline we can't build).
      if (this._rawCameraTrack) await this.transport?.replaceVideoTrack?.(this._rawCameraTrack);
      if (!this.cfg.platform.createVideoEffectPipeline && !isNoOpEffect(next)) {
        console.warn('ZeroRate RTC: this platform adapter has no createVideoEffectPipeline; effect not applied.');
      }
      return;
    }

    this._rawCameraTrack = this._rawCameraTrack ?? sourceTrack;
    const pipeline = this.cfg.platform.createVideoEffectPipeline(this._rawCameraTrack, next);
    // Build a local preview stream (processed video + existing audio) so the presenter
    // sees the effect too, not just remote peers. Falls back gracefully where the adapter
    // can't construct a MediaStream (the published track still carries the effect).
    const audioTracks = this._localStream?.getAudioTracks?.() ?? [];
    const previewStream = this.cfg.platform.createMediaStream?.([pipeline.track, ...audioTracks]);
    this.effectPipeline = { ...pipeline, sourceTrack: this._rawCameraTrack, previewStream };
    await this.transport?.replaceVideoTrack?.(pipeline.track);
  }

  /** True when E2E encryption is actually engaged on the live media (not just requested). */
  get e2eeActive(): boolean {
    return this._e2eeActive;
  }

  // ---------------------------------------------------------------------------
  // Scheduling — plan a group call & share a calendar invite ("Schedule group calls").
  // ---------------------------------------------------------------------------

  /** Build a scheduled-call descriptor (pure; the app persists/shares it as it wishes). */
  schedule(input: Omit<CreateScheduledCallInput, 'host'> & { host?: string }): ScheduledCall {
    return createScheduledCall({ ...input, host: input.host ?? this.cfg.selfId ?? 'me' });
  }

  /** Render an RFC-5545 calendar invite (.ics body) for a scheduled call. */
  scheduledCallICS(call: ScheduledCall, joinUrl?: string): string {
    return toICS(call, joinUrl);
  }

  /** Join a previously-scheduled call's room (silent group join, no ringing). */
  async joinScheduled(call: ScheduledCall): Promise<void> {
    await this.join({ roomId: call.roomId, mode: 'group', password: call.password, max: call.invitees.length + 1 });
  }

  /** StreamYard-style multistream controls. */
  readonly broadcast = {
    goLive: async (targets: BroadcastTarget[]): Promise<void> => {
      if (!this.cfg.whipEndpoint) {
        throw new Error('whipEndpoint is required to go live');
      }
      const controller = new BroadcastController(
        new WhipClient(this.cfg.whipEndpoint, this.cfg.mediaToken || this.cfg.token),
      );
      await controller.goLive(targets);
    },
    stop: async (): Promise<void> => {
      if (!this.cfg.whipEndpoint) return;
      const controller = new BroadcastController(
        new WhipClient(this.cfg.whipEndpoint, this.cfg.mediaToken || this.cfg.token),
      );
      await controller.stop();
    },
  };
}

export function createClient(cfg: ZrClientConfig): ZrClient {
  return new ZrClient(cfg);
}

/**
 * Product-oriented entry point that maps the documented instantiation parameters (the parsed
 * variables) onto the engine and pins the correct plane per plan — the sequence is:
 *
 *   1. Resolve the product profile (Connectra Basic → Raw P2P; Connectra+ → WHIP/WHEP media manager)
 *      and validate the config (fails loud on a mismatch, e.g. a contact-centre role on Raw P2P).
 *   2. Build the ZrClient, which auto-connects the signaling/presence plane and, on join(), mints the
 *      region-scoped CoTURN/ICE credentials and maintains the keep-alive.
 *
 * The app still supplies the `signaling` + `platform` adapters (and a `token` mint hook); everything
 * else is derived from these variables so web-to-native and native-to-web share one call.
 */
export function createConnectra(params: {
  roomId: string;
  participantId: string;
  roomPassword?: string;
  appPublicKey?: string;
  region?: string;
  authUrl?: string;
  callExperience?: CallUxChoice;
  billingMode?: BillingMode;
  peerRole?: PeerRole;
  availability?: AvailabilityStatus;
  provider?: SignalingProvider;
  signaling: SignalingClient;
  platform: PlatformAdapter;
  ice?: IceConfig;
  token: string;
} & Partial<ZrClientConfig>): ZrClient {
  const profile = resolveProductProfile({ billingMode: params.billingMode, peerRole: params.peerRole, provider: params.provider });
  validateProductConfig({ billingMode: params.billingMode, peerRole: params.peerRole, provider: params.provider });
  const { roomId, participantId, roomPassword, appPublicKey, region, authUrl, callExperience, ...rest } = params;
  return new ZrClient({
    ...rest,
    selfId: participantId,
    provider: profile.provider,
    publicAppKey: appPublicKey,
    regionCode: region,
    authBaseUrl: authUrl,
    ui: { style: callExperience ?? 'auto', glass: true },
    ice: params.ice ?? { iceServers: [] },
  });
}

export * from 'zr-core';
export { resolveProvider } from 'zr-webrtc';
export type { BroadcastTarget, PlatformAdapter, IceConfig, ScreenComposite, ScreenCompositeOptions, ResolvedProvider } from 'zr-webrtc';
